import { useState } from 'react';
import { CpmButton } from '../../../components/design-system';
import {
    getReportsAdminErrorMessage,
    reportsAdminErrorReason,
    updateStoreBrandNameOverride,
} from '../../../services/reportsAdminService';
import type { FormPhase } from '../../../types/reportsAdmin.types';
import { REPORTS_COPY, REPORTS_TIPS } from '../copy';
import { REPORTS_INPUT } from '../fieldStyles';
import InfoI from '../components/InfoI';
import { ReportsWait } from '../components/ReportsDialog';

interface NameOverrideRowProps {
    storeBrandID: string;
    reportID: string;
    catalogueName: string;
    value: string | null | undefined;
    onSaved: (next: string | null) => void;
}

export default function NameOverrideRow({
    storeBrandID,
    reportID,
    catalogueName,
    value,
    onSaved,
}: NameOverrideRowProps) {
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState(value ?? '');
    const [phase, setPhase] = useState<FormPhase>('idle');
    const [error, setError] = useState('');
    const [ok, setOk] = useState('');

    const saveFailReason = reportsAdminErrorReason(
        error,
        'Nothing was written. What you typed is still here, and what shops see has not changed.'
    );

    const persist = async (next: string) => {
        setPhase('saving');
        setError('');
        setOk('');
        try {
            const result = await updateStoreBrandNameOverride({
                storeBrandID,
                reportID,
                nameOverride: next,
            });
            onSaved(result.nameOverride);
            setDraft(result.nameOverride ?? '');
            setOpen(false);
            setPhase('idle');
            setOk(
                result.nameOverride
                    ? `Saved. Shops see “${result.nameOverride}” for this store brand — the file they download is unchanged.`
                    : 'Saved as blank. Shops see the catalogue name for this store brand.'
            );
        } catch (err) {
            setError(getReportsAdminErrorMessage(err));
            setPhase('idle');
        }
    };

    return (
        <div>
            <div className="flex items-center flex-wrap" style={{ gap: 7 }}>
                <span
                    className="uppercase"
                    style={{
                        fontSize: 9.5,
                        fontWeight: 800,
                        letterSpacing: '0.07em',
                        color: 'var(--cpm-text-tertiary)',
                    }}
                >
                    {REPORTS_COPY.REPORT_NAME_FOR_BRAND}
                </span>
                <InfoI
                    title={REPORTS_TIPS.NAME_OVERRIDE.title}
                    body={REPORTS_TIPS.NAME_OVERRIDE.body}
                />
            </div>
            <div className="flex items-center flex-wrap" style={{ gap: 10, marginTop: 6 }}>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--cpm-text-primary)' }}>
                    {value ? `“${value}”` : `Blank — this store brand sees “${catalogueName}”`}
                </span>
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setDraft(value ?? '');
                        setError('');
                        setOpen(true);
                    }}
                    className="cursor-pointer"
                    style={{
                        padding: '4px 10px',
                        borderRadius: 8,
                        border: '1px solid var(--cpm-border-default)',
                        background: '#fff',
                        fontSize: 11.5,
                        fontWeight: 800,
                        color: 'var(--cpm-text-secondary)',
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = 'var(--cpm-brand-purple)';
                        e.currentTarget.style.color = 'var(--cpm-brand-navy)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = 'var(--cpm-border-default)';
                        e.currentTarget.style.color = 'var(--cpm-text-secondary)';
                    }}
                >
                    {value ? 'Change this name' : 'Set a name'}
                </button>
            </div>
            {ok && !open ? (
                <div
                    style={{
                        marginTop: 6,
                        fontSize: 11.5,
                        fontWeight: 700,
                        lineHeight: 1.45,
                        color: 'var(--cpm-success-text)',
                    }}
                >
                    {ok}
                </div>
            ) : null}
            {open ? (
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        marginTop: 9,
                        padding: 12,
                        border: '1.5px solid var(--cpm-border-strong)',
                        borderRadius: 10,
                        background: 'var(--cpm-surface-subtle)',
                    }}
                >
                    <input
                        value={draft}
                        placeholder="Blank — shops see the catalogue name"
                        onChange={(e) => setDraft(e.target.value)}
                        style={REPORTS_INPUT}
                    />
                    <div
                        style={{
                            marginTop: 7,
                            fontSize: 11.5,
                            fontWeight: 600,
                            lineHeight: 1.45,
                            color: 'var(--cpm-text-secondary)',
                        }}
                    >
                        Blank is a valid value: this store brand then sees the catalogue name. A
                        saved name is the only name shops see on Generate Report for this store
                        brand — on the cards and in the details.
                    </div>
                    {error ? (
                        <div style={{ marginTop: 9 }}>
                            <div
                                style={{
                                    fontSize: 12,
                                    fontWeight: 800,
                                    color: 'var(--cpm-shortfall-text)',
                                }}
                            >
                                Couldn’t save this name
                            </div>
                            <div
                                style={{
                                    marginTop: 3,
                                    fontSize: 11.5,
                                    fontWeight: 600,
                                    color: 'var(--cpm-text-secondary)',
                                }}
                            >
                                Nothing was written. What you typed is still here, and what shops
                                see has not changed.
                                {saveFailReason ? (
                                    <div style={{ marginTop: 6 }}>{saveFailReason}</div>
                                ) : null}
                            </div>
                        </div>
                    ) : null}
                    {phase === 'saving' ? (
                        <div style={{ marginTop: 10 }}>
                            <ReportsWait label="Saving the report name for this store brand…" />
                        </div>
                    ) : (
                        <div
                            className="flex items-center flex-wrap"
                            style={{ gap: 9, marginTop: 10 }}
                        >
                            <CpmButton variant="navy" size="sm" onClick={() => void persist(draft)}>
                                {error ? 'Retry the save' : REPORTS_COPY.SAVE_THIS_NAME}
                            </CpmButton>
                            <CpmButton
                                variant="ghost"
                                size="sm"
                                disabled={!value}
                                onClick={() => void persist('')}
                            >
                                {REPORTS_COPY.CLEAR_THE_NAME}
                            </CpmButton>
                            <CpmButton
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    setOpen(false);
                                    setError('');
                                }}
                            >
                                {REPORTS_COPY.CANCEL}
                            </CpmButton>
                        </div>
                    )}
                </div>
            ) : null}
        </div>
    );
}
